import Events from '../events/Events';
import { TextAreaContent } from '../quill/converters/types';
import { ISelectionRange, ITextAttributes } from '../quill/types';
import QuillImplementation from '../quill/QuillImplementation';
import IRichTextField from '../IRichTextField';
import { IRichTextFieldConverters, IRichTextFieldOptions, IRichTextFieldOptionsInternal } from './IRichTextFieldOptions';
import resolveElement from './resolveElement';

const DEFAULT_OPTIONS = {
    applyToWholeTextFieldWithNoSelection: false,
    removeNonFontStyleFormatting: true,
};

/**
 * The original contributors of cimdoc-rtf believed this layer would
 * help allow them to switch the implementation internally to cimdoc-rtf
 * while maintaining a singular interface for consumers of cimdoc-rtf
 * At the moment the only implementation is via QuillImplementation
 */
export default class RichTextField extends IRichTextField {
    static setConverters(richTextFieldConverters: Partial<IRichTextFieldConverters>) {
        QuillImplementation.setConverters(richTextFieldConverters);
    }

    public el: HTMLElement;
    private implementation: IRichTextField;
    private options: IRichTextFieldOptionsInternal;

    constructor(element: HTMLElement | string, options: IRichTextFieldOptions = {}) {
        super();

        this.options = { ...DEFAULT_OPTIONS, ...options };

        this.el = resolveElement(element);
        this.implementation = new QuillImplementation(this.el, this.options);

        // Disable translation for editor contents
        const editor = this.el.querySelector('.ql-editor');
        if (editor) {
            editor.classList.add('notranslate');
            editor.setAttribute('translate', 'no');
        }

        // proxy events from the implementation
        this.implementation.addEventListener(Events.All, this.dispatchEvent.bind(this));

        if (options.initialTextAreaContent) {
            this.setTextAreaContent(options.initialTextAreaContent);
        }
    }

    public getCommonAttributes(): {[key: string]: any} {
        return this.implementation.getCommonAttributes();
    }

    public getAllAttributes(): {[key: string]: any[]} {
        return this.implementation.getAllAttributes();
    }

    public setAttribute(name: string, value: any) {
        // first focus the editor to make sure we can get the current selection
        this.implementation.setAttribute(name, value);
    }

    public setAttributes(attributes: ITextAttributes) {
        this.implementation.setAttributes(attributes);
    }

    public getTextAreaContent(): TextAreaContent[] {
        return this.implementation.getTextAreaContent();
    }

    public setTextAreaContent(content: TextAreaContent[]) {
        this.implementation.setTextAreaContent(content);
    }

    public getSelection() {
        return this.implementation.getSelection();
    }

    public setSelection(selection: ISelectionRange | null) {
        this.implementation.setSelection(selection);
    }

    public getRangeTextAreaContent(range: ISelectionRange): TextAreaContent[] {
        return this.implementation.getRangeTextAreaContent(range);
    }

    public getLength() {
        return this.implementation.getLength();
    }

    public focus() {
        this.implementation.focus();
    }

    public blur() {
        this.implementation.blur();
    }

    public enable(enabled = true) {
        this.implementation.enable(enabled);
    }

    public insert(text: string, index: number) {
        this.implementation.insert(text, index);
    }

    public delete(length = 1, index: number) {
        this.implementation.delete(length, index);
    }

    public updatePlaceholder(placeholder: string) {
        this.implementation.updatePlaceholder(placeholder);
    }

    public rerender() {
        this.implementation.rerender();
    }
}
